import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import api from '../../../api/axios';
import { IApiResponseError, IApiState } from '../../../types/api';
import { RootState } from '../..';

export interface IGetGiftCardPricesResponse {
  status?: boolean;
  message?: string;
  data?: {
    id?: string;
    price_type?: string;
    country_code?: string;
    currency_symbol?: string;
    conversionRate?: number;
    price?: {
      min_price?: number;
      max_price?: number;
      fixed_price?: string;
    };
  }[];
  code?: number;
  errors?: IApiResponseError[];
}

const initialState: IApiState<IGetGiftCardPricesResponse> = {
  value: null,
  status: 'idle',
  error: '',
};

export const getGiftCardPricesAsync = createAsyncThunk(
  'giftCards/giftCardPrices',
  async (priceId: string, { rejectWithValue, getState }) => {
    try {
      const state = getState() as RootState;

      const accessToken = state.auth.value?.data?.token?.accessToken;

      const businessId = localStorage.getItem('businessId');

      const config = {
        headers: {
          Authorization: `Token ${accessToken}`,
          'X-Business-Id': businessId,
        },
      };

      const { data } = await api.get(`gift-card/prices/${priceId}`, config);

      return data;
    } catch (err) {
      const error = err as AxiosError<IApiResponseError>;

      return rejectWithValue(error.response?.data);
    }
  }
);

export const giftCardPricesGetSlice = createSlice({
  name: 'giftCards/giftCardPrices/get',
  initialState,
  reducers: {
    resetGetGiftCardPrices: (state) => {
      state.value = null;
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getGiftCardPricesAsync.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(getGiftCardPricesAsync.fulfilled, (state, action) => {
        state.status = 'success';
        state.value = action.payload as IGetGiftCardPricesResponse;
      })
      .addCase(getGiftCardPricesAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.value = action.payload as IGetGiftCardPricesResponse;
        state.error = action.error.message;
      });
  },
});

export const { resetGetGiftCardPrices } = giftCardPricesGetSlice.actions;

export default giftCardPricesGetSlice.reducer;
